import React, {
   useContext,
   useEffect,
   useMemo,
   useRef,
   useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";

import { fetchInstructors } from "../../store/instructorsSlice";
import { fetchUsers } from "../../store/usersSlice";
import { UserContext } from "../../UserContext";
import {
   getInstructorTypeLabel,
   INSTRUCTOR_TYPE_OPTIONS,
   normalizeInstructorExam,
   normalizeInstructorType,
} from "../../constants/instructors";
import { openPopup as openPopupUI } from "../Common/popupUIStore";
import UIIcon from "../Common/UIIcon";

import SimpleDropdown from "../Common/SimpleDropdown";
import StudentItem from "../Common/StudentItem";

const PAGE_SIZE = 24;
const PAGE_SIZE_MOBILE = 8;
const MOBILE_BP = 768;

const FILTER_OPTIONS = [
   { value: "ALL", label: "Toți" },
   { value: "INSTRUCTOR", label: "Instructori" },
   ...INSTRUCTOR_TYPE_OPTIONS.map((option) => ({
      value: `INSTRUCTOR_TYPE:${option.value}`,
      label: `Instr. ${option.label}`,
   })),
   { value: "ADMIN", label: "Admini" },
   { value: "MANAGER", label: "Manageri" },
   { value: "PROFESSOR", label: "Profesori" },
];

const ROLE_LABELS = {
   INSTRUCTOR: "Instructor",
   MANAGER: "Manager",
   ADMIN: "Admin",
   PROFESSOR: "Profesor",
};

const ROLE_ORDER = {
   INSTRUCTOR: 0,
   ADMIN: 1,
   MANAGER: 2,
   PROFESSOR: 3,
};

const CREATE_BUTTONS = ["INSTRUCTOR", "ADMIN", "MANAGER", "PROFESSOR"];
const CREATE_MENU_PLACEHOLDER = "__none__";
const CREATE_DROPDOWN_OPTIONS = [
   { value: "INSTRUCTOR", label: "Instructor" },
   { value: "ADMIN", label: "Admin" },
   { value: "MANAGER", label: "Manager" },
   { value: "PROFESSOR", label: "Profesor" },
];

const AVATAR_HUES = [
   { h: 70, s: 75 },
   { h: 0, s: 100 },
   { h: 30, s: 100 },
   { h: 54, s: 95 },
   { h: 130, s: 65 },
   { h: 210, s: 90 },
   { h: 255, s: 98 },
   { h: 285, s: 100 },
   { h: 330, s: 96 },
];

const AVATAR_LIGHTNESSES = [94, 92, 90, 88, 86, 84, 82, 80, 78, 76];

const AVATAR_COLORS = AVATAR_HUES.flatMap(({ h, s }) =>
   AVATAR_LIGHTNESSES.map((l) => `hsl(${h} ${s}% ${l}%)`),
);

function isUnauthorizedErrorMessage(value) {
   const message = String(value || "").toLowerCase();
   if (!message) return false;

   return (
      message.includes("unauthorized") ||
      message.includes("statuscode\":401") ||
      message.includes("status 401") ||
      message.includes("http 401")
   );
}

function useIsMobile(bp = MOBILE_BP) {
   const [isMobile, setIsMobile] = useState(() => {
      if (typeof window === "undefined") return false;
      return window.matchMedia(`(max-width:${bp}px)`).matches;
   });

   useEffect(() => {
      if (typeof window === "undefined") return undefined;

      const mq = window.matchMedia(`(max-width:${bp}px)`);
      const onChange = (event) => setIsMobile(event.matches);

      if (mq.addEventListener) mq.addEventListener("change", onChange);
      else mq.addListener(onChange);

      return () => {
         if (mq.removeEventListener) mq.removeEventListener("change", onChange);
         else mq.removeListener(onChange);
      };
   }, [bp]);

   return isMobile;
}

function normalizeRole(value) {
   return String(value || "")
      .trim()
      .toUpperCase();
}

function hashStringToUInt(str) {
   let hash = 0;

   for (let i = 0; i < str.length; i += 1) {
      hash = (hash * 31 + str.charCodeAt(i)) | 0;
   }

   return hash >>> 0;
}

function getHashedColor(firstName, lastName) {
   const fullName = `${firstName || ""} ${lastName || ""}`.trim();
   const hasLetter = /\p{L}/u.test(fullName);

   if (!hasLetter) return AVATAR_COLORS[0];

   const index =
      hashStringToUInt(fullName.normalize("NFKD")) % AVATAR_COLORS.length;
   return AVATAR_COLORS[index];
}

function getInitials(firstName, lastName) {
   const first = String(firstName || "")
      .trim()
      .charAt(0);
   const last = String(lastName || "")
      .trim()
      .charAt(0);

   if (first && last) return `${first}${last}`.toUpperCase();
   if (first) return first.toUpperCase();
   if (last) return last.toUpperCase();

   return "–";
}

function normalizeColorToCss(value, fallback) {
   const raw = String(value || "").trim();

   if (!raw) return fallback;
   if (raw.startsWith("var(")) return raw;
   if (raw.startsWith("--")) {
      const short = raw.slice(2).trim().toLowerCase();
      if (!short || short === "default") return "var(--event-default)";
      if (short.startsWith("event-")) return `var(--${short})`;
      return `var(--event-${short})`;
   }
   if (/^event-/i.test(raw)) return `var(--${raw.toLowerCase()})`;
   return raw;
}

export default function PersonalManager() {
   const dispatch = useDispatch();
   const { user } = useContext(UserContext);
   const {
      list: instructors = [],
      status: instructorsStatus = "idle",
      error: instructorsError = null,
   } = useSelector((state) => state.instructors || {});
   const {
      list: users = [],
      status: usersStatus = "idle",
      error: usersError = null,
   } = useSelector((state) => state.users || {});

   const isMobile = useIsMobile(MOBILE_BP);
   const isAdmin = normalizeRole(user?.role) === "ADMIN";
   const pageSize = isMobile ? PAGE_SIZE_MOBILE : PAGE_SIZE;

   const [search, setSearch] = useState("");
   const [filter, setFilter] = useState("ALL");
   const [visibleCount, setVisibleCount] = useState(pageSize);
   const searchInputRef = useRef(null);

   useEffect(() => {
      if (instructorsStatus === "idle" || instructorsStatus === "failed") {
         dispatch(fetchInstructors());
      }
      if (usersStatus === "idle" || usersStatus === "failed") {
         dispatch(fetchUsers());
      }
   }, [dispatch, instructorsStatus, usersStatus]);

   const usersById = useMemo(() => {
      const map = new Map();

      for (const user of users || []) {
         const key = String(user?.id ?? "").trim();
         if (!key) continue;
         map.set(key, user);
      }

      return map;
   }, [users]);

   const personnel = useMemo(() => {
      const items = [];

      for (const instructor of Array.isArray(instructors) ? instructors : []) {
         const linkedUser = usersById.get(String(instructor?.userId ?? ""));
         const firstName = instructor?.firstName || "";
         const lastName = instructor?.lastName || "";
         const email = String(
            linkedUser?.email || instructor?.email || "",
         ).trim();
         const phone = String(instructor?.phone || "").trim();
         const sector = String(instructor?.sector || "").trim();
         const instructorType = normalizeInstructorType(
            instructor?.instructorType,
         );
         const instructorTypeLabel = getInstructorTypeLabel(instructorType);
         const isExamen = normalizeInstructorExam(instructor?.isExamen);
         const fullName = `${firstName} ${lastName}`.trim();
         const fallbackColor = getHashedColor(firstName, lastName);
         const rawColor = String(
            instructor?.color || linkedUser?.color || "",
         ).trim();

         items.push({
            id: `instructor-${instructor?.id ?? fullName}`,
            kind: "INSTRUCTOR",
            roleLabel: ROLE_LABELS.INSTRUCTOR,
            firstName,
            lastName,
            fullName,
            phone,
            email,
            color: rawColor,
            instructorType,
            isExamen,
            displayColor: normalizeColorToCss(rawColor, fallbackColor),
            initials: getInitials(firstName, lastName),
            secondaryText:
               [
                  ROLE_LABELS.INSTRUCTOR,
                  instructorTypeLabel,
                  isExamen ? "Examen" : null,
                  phone,
                  sector || email,
               ]
                  .filter(Boolean)
                  .join(" • ") || "Instructor",
            searchText:
               `${fullName} ${phone} ${email} ${sector} ${rawColor} ${instructorType} ${instructorTypeLabel} ${isExamen ? "examen" : ""}`.toLowerCase(),
            record: instructor,
         });
      }

      for (const user of Array.isArray(users) ? users : []) {
         const role = normalizeRole(user?.role);

         if (
            role !== "MANAGER" &&
            role !== "ADMIN" &&
            role !== "PROFESSOR"
         ) {
            continue;
         }

         const firstName = user?.firstName || "";
         const lastName = user?.lastName || "";
         const fullName = `${firstName} ${lastName}`.trim();
         const phone = String(user?.phone || "").trim();
         const email = String(user?.email || "").trim();
         const fallbackColor = getHashedColor(firstName, lastName);
         const supportsColor = role === "MANAGER" || role === "ADMIN";
         const rawColor = supportsColor ? String(user?.color || "").trim() : "";

         items.push({
            id: `${role.toLowerCase()}-${user?.id ?? fullName}`,
            kind: role,
            roleLabel: ROLE_LABELS[role] || role,
            firstName,
            lastName,
            fullName,
            phone,
            email,
            color: rawColor,
            displayColor: normalizeColorToCss(rawColor, fallbackColor),
            initials: getInitials(firstName, lastName),
            secondaryText:
               [ROLE_LABELS[role] || role, phone, email]
                  .filter(Boolean)
                  .join(" • ") ||
               ROLE_LABELS[role] ||
               role,
            searchText:
               `${fullName} ${phone} ${email} ${role} ${rawColor}`.toLowerCase(),
            record: user,
         });
      }

      return items.sort((left, right) => {
         const roleDiff =
            (ROLE_ORDER[left.kind] ?? 99) - (ROLE_ORDER[right.kind] ?? 99);

         if (roleDiff !== 0) return roleDiff;

         return left.fullName.localeCompare(right.fullName, "ro", {
            sensitivity: "base",
         });
      });
   }, [instructors, users, usersById]);

   const normalizedQuery = useMemo(
      () =>
         String(search || "")
            .trim()
            .toLowerCase(),
      [search],
   );

   const filteredPersonnel = useMemo(
      () =>
         personnel.filter((entry) => {
            const roleMatches =
               filter === "ALL" ||
               entry.kind === filter ||
               (String(filter).startsWith("INSTRUCTOR_TYPE:") &&
                  entry.kind === "INSTRUCTOR" &&
                  normalizeInstructorType(entry.instructorType) ===
                     String(filter).split(":")[1]);
            const searchMatches =
               !normalizedQuery || entry.searchText.includes(normalizedQuery);
            return roleMatches && searchMatches;
         }),
      [filter, normalizedQuery, personnel],
   );

   useEffect(() => {
      setVisibleCount(pageSize);
   }, [filter, normalizedQuery, pageSize]);

   const visiblePersonnel = useMemo(
      () => filteredPersonnel.slice(0, visibleCount),
      [filteredPersonnel, visibleCount],
   );

   const totalResults = filteredPersonnel.length;
   const canLoadMore = visibleCount < totalResults;
   const nextBatch = Math.min(
      pageSize,
      Math.max(0, totalResults - visibleCount),
   );
   const usersUnauthorized = isUnauthorizedErrorMessage(usersError);
   const hasData = personnel.length > 0;
   const hasError = hasData
      ? null
      : instructorsError || (usersUnauthorized ? null : usersError);
   const isLoading =
      instructorsStatus === "loading" || usersStatus === "loading";
   const filterOptions = useMemo(
      () =>
         usersUnauthorized
            ? FILTER_OPTIONS.filter(
                 (option) =>
                    option.value === "ALL" ||
                    option.value === "INSTRUCTOR" ||
                    String(option.value).startsWith("INSTRUCTOR_TYPE:"),
              )
            : FILTER_OPTIONS,
      [usersUnauthorized],
   );
   const createOptions = useMemo(
      () =>
         CREATE_DROPDOWN_OPTIONS.filter(
            (option) => option.value !== "ADMIN" || isAdmin,
         ),
      [isAdmin],
   );

   useEffect(() => {
      if (!filterOptions.some((option) => option.value === filter)) {
         setFilter("ALL");
      }
   }, [filter, filterOptions]);

   const handleLoadMore = () => {
      setVisibleCount((current) => Math.min(current + pageSize, totalResults));
   };

   const handleFocusSearch = () => {
      searchInputRef.current?.focus();
   };

   const handleClearSearch = () => {
      setSearch("");
      searchInputRef.current?.focus();
   };

   const handleOpenCreate = (role = null) => {
      if (role === "ADMIN" && !isAdmin) return;

      openPopupUI("personalCreate", {
         initialRole: role,
      });
   };

   const handleCreateSelect = (value) => {
      if (value === "ADMIN" && !isAdmin) {
         return;
      }

      if (CREATE_BUTTONS.includes(value)) {
         handleOpenCreate(value);
      }
   };

   const handleOpenEntry = (entry) => {
      if (entry.kind === "INSTRUCTOR") {
         openPopupUI("instructorDetails", { instructor: entry.record });
         return;
      }

      if (entry.kind === "MANAGER" || entry.kind === "ADMIN") {
         openPopupUI("managerAdminDetails", { user: entry.record });
         return;
      }

      if (entry.kind === "PROFESSOR") {
         openPopupUI("professorDetails", { user: entry.record });
      }
   };

   return (
      <section className="personalPageUI">
         <div className="personalPageUI__header">
            <div className="personalPageUI__titleWrap">
               <h2 className="personalPageUI__title">Personal</h2>
            </div>

            <div className="personalPageUI__controls">
               <SimpleDropdown
                  value={filter}
                  onChange={setFilter}
                  options={filterOptions}
                  placeholder="Categorie"
                  className="personalPageUI__dropdown"
                  aria-label="Filtru personal"
               />
               <SimpleDropdown
                  value={CREATE_MENU_PLACEHOLDER}
                  onChange={handleCreateSelect}
                  options={createOptions}
                  placeholder="Adaugă"
                  className="personalPageUI__dropdown personalPageUI__dropdown--create"
                  aria-label="Adaugă personal"
               />
            </div>
         </div>

         <div className="personalPageUI__toolbar">
            <div className="personalPageUI__search">
               <div className="personalPageUI__searchInputWrap">
                  <input
                     ref={searchInputRef}
                     type="search"
                     className="personalPageUI__searchInput"
                     placeholder="Caută personal..."
                     value={search}
                     onChange={(event) => setSearch(event.target.value)}
                  />
                  <button
                     type="button"
                     className="personalPageUI__searchClear"
                     onClick={handleClearSearch}
                     disabled={!search}
                     aria-label="Șterge căutarea"
                     title="Șterge căutarea"
                  >
                     <UIIcon
                        name="close"
                        className="personalPageUI__searchIcon personalPageUI__searchIcon--clear"
                     />
                  </button>
               </div>

               <button
                  type="button"
                  className="personalPageUI__searchButton"
                  onClick={handleFocusSearch}
                  aria-label="Caută"
                  title="Caută"
               >
                  <UIIcon
                     name="search"
                     className="personalPageUI__searchIcon"
                  />
               </button>
            </div>
         </div>

         <div className="personalPageUI__gridWrap">
            <div className="personalPageUI__grid">
               {isLoading && (
                  <p className="personalPageUI__status">
                     Se încarcă lista de personal...
                  </p>
               )}

               {!isLoading && hasError && (
                  <p className="personalPageUI__status personalPageUI__status--error">
                     {hasError}
                  </p>
               )}

               {!isLoading &&
                  !hasError &&
                  visiblePersonnel.map((entry) => (
                     <StudentItem
                        key={entry.id}
                        student={entry}
                        color={entry.displayColor}
                        initials={entry.initials}
                        onOpen={() => handleOpenEntry(entry)}
                        highlightQuery={search}
                        secondaryText={entry.secondaryText}
                        className={`personalPageUI__item personalPageUI__item--${entry.kind.toLowerCase()}`}
                     />
                  ))}

               {!isLoading && !hasError && totalResults > 0 && (
                  <div className="personalPageUI__footer">
                     <span className="personalPageUI__counter">
                        Afișate {Math.min(visibleCount, totalResults)} din{" "}
                        {totalResults}
                     </span>

                     {canLoadMore && (
                        <button
                           type="button"
                           className="personalPageUI__loadMore"
                           onClick={handleLoadMore}
                        >
                           Afișează încă {nextBatch}
                        </button>
                     )}
                  </div>
               )}

               {!isLoading && !hasError && totalResults === 0 && (
                  <p className="personalPageUI__empty">
                     Nu există rezultate pentru filtrul curent.
                  </p>
               )}
            </div>
         </div>
      </section>
   );
}
